import type { Virtualizer } from "@tanstack/react-virtual";

// constants for scroll behavior
const BOTTOM_THRESHOLD_PAGES = 3;

type ScrollToPageOptions = {
  pageIndex: number;
  totalPages: number;
  virtualizer: Virtualizer<HTMLDivElement, Element>;
};

/**
 * scrolls to a page in the virtualizer
 * when near the bottom, scrolls to absolute bottom to avoid virtualizer alignment issues
 * otherwise, uses center alignment for optimal visibility
 */
export const scrollToPage = ({
  pageIndex,
  totalPages,
  virtualizer,
}: ScrollToPageOptions): void => {
  const isNearBottom = pageIndex >= totalPages - BOTTOM_THRESHOLD_PAGES;

  if (!isNearBottom) {
    virtualizer.scrollToIndex(pageIndex, { align: "center" });
    return;
  }

  // when near bottom, directly set scroll position to avoid virtualizer issues
  const scrollElement = virtualizer.scrollElement;
  if (!scrollElement) return;

  const maxScroll = scrollElement.scrollHeight - scrollElement.clientHeight;
  scrollElement.scrollTop = maxScroll;
};

/**
 * safely scrolls to a page after ensuring the virtualizer is ready.
 * uses requestAnimationFrame to prevent race conditions during mount.
 */
export const scrollToPageSafe = (options: ScrollToPageOptions): void => {
  requestAnimationFrame(() => scrollToPage(options));
};
